import { useEffect, useState } from "react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, FileText, Clock, CheckCircle2, XCircle, Video } from "lucide-react";

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-6">
      <Icon className={`w-6 h-6 ${tone || "text-primary"}`} />
      <div className="font-serif text-3xl mt-4">{value}</div>
      <div className="text-sm text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

const STATUS_STYLE = {
  pending:  { label: "Pending",  cls: "bg-amber-100 text-amber-800 border-amber-200" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [tab, setTab] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const loadStats = async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch {}
  };
  const loadApps = async (status = tab) => {
    try {
      const { data } = await api.get("/admin/applications", { params: { status } });
      setApps(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => { loadStats(); loadApps("pending"); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadApps(tab); /* eslint-disable-next-line */ }, [tab]);

  const decide = async (application_id, status) => {
    try {
      await api.put(`/admin/applications/${application_id}`, { status, admin_notes: notes });
      toast.success(`Marked ${status}`);
      setSelected(null); setNotes("");
      loadStats(); loadApps(tab);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <div className="mb-8">
        <div className="text-primary text-xs uppercase tracking-widest font-semibold">Casting HQ</div>
        <h1 className="font-serif text-4xl md:text-5xl mt-1">Admin dashboard</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label="Users" value={stats.users} />
          <StatCard icon={FileText} label="Profiles" value={stats.profiles} />
          <StatCard icon={Clock} label="Pending applications" value={stats.applications.pending} tone="text-amber-600" />
          <StatCard icon={CheckCircle2} label="Approved" value={stats.applications.approved} tone="text-emerald-600" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full h-auto p-1 bg-secondary">
          <TabsTrigger data-testid="admin-tab-pending" value="pending" className="rounded-full">Pending</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-approved" value="approved" className="rounded-full">Approved</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-rejected" value="rejected" className="rounded-full">Rejected</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-all" value="all" className="rounded-full">All</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-6">
          {apps.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground bg-card border border-border rounded-2xl">No applications here.</div>
          ) : (
            <div className="grid gap-4">
              {apps.map((a) => (
                <div key={a.application_id} data-testid={`admin-app-${a.application_id}`} className="bg-card border border-border rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="font-serif text-xl">{a.full_name}, {a.age}</div>
                      <Badge className={`rounded-full font-normal border ${STATUS_STYLE[a.status].cls}`}>{STATUS_STYLE[a.status].label}</Badge>
                      {a.video_path && (
                        <Badge data-testid={`admin-has-video-${a.application_id}`} className="rounded-full font-normal border bg-primary/10 text-primary border-primary/20">
                          <Video className="w-3 h-3 mr-1" /> Video
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">{a.city}, {a.province} · {a.email}</div>
                    <div className="text-sm mt-3 line-clamp-2 text-foreground/80">{a.why_pick_me}</div>
                  </div>
                  <Dialog onOpenChange={(o) => { if (o) { setSelected(a); setNotes(a.admin_notes || ""); } }}>
                    <DialogTrigger asChild>
                      <Button data-testid={`admin-review-${a.application_id}`} variant="outline" className="rounded-full">Review</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      {selected && (
                        <>
                          <DialogHeader>
                            <DialogTitle className="font-serif text-2xl">{selected.full_name}, {selected.age}</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 text-sm">
                            <div className="grid grid-cols-2 gap-4">
                              <div><div className="text-xs text-muted-foreground">Location</div>{selected.city}, {selected.province}</div>
                              <div><div className="text-xs text-muted-foreground">Contact</div>{selected.email} · {selected.phone}</div>
                              <div><div className="text-xs text-muted-foreground">Available</div>{selected.availability}</div>
                              <div><div className="text-xs text-muted-foreground">Social</div>{selected.social_handle || "—"}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">Why pick them</div>
                              <div className="bg-secondary/50 rounded-xl p-3">{selected.why_pick_me}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">Ideal partner</div>
                              <div className="bg-secondary/50 rounded-xl p-3">{selected.ideal_partner}</div>
                            </div>
                            {selected.video_path && (
                              <div>
                                <div className="text-xs text-muted-foreground mb-1">Video pitch</div>
                                <video
                                  controls
                                  data-testid={`admin-video-${selected.application_id}`}
                                  className="w-full rounded-xl bg-black max-h-80"
                                  src={fileUrl(selected.video_path)}
                                />
                              </div>
                            )}
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">Admin notes</div>
                              <Textarea data-testid="admin-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-20 rounded-xl" placeholder="Internal notes…" />
                            </div>
                            <div className="flex gap-2 justify-end pt-2">
                              <Button data-testid="admin-reject-btn" variant="outline" onClick={() => decide(selected.application_id, "rejected")} className="rounded-full">
                                <XCircle className="w-4 h-4 mr-1" /> Reject
                              </Button>
                              <Button data-testid="admin-approve-btn" onClick={() => decide(selected.application_id, "approved")} className="rounded-full bg-primary hover:bg-primary/90">
                                <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </DialogContent>
                  </Dialog>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
